import FeedbackPage from '@app-studio-web/ui/pages/FeedbackPage'
import QuestionPage from '@app-studio-web/ui/pages/QuestionPage'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useSubmit } from 'react-router-dom'
import useUpdateMode from '../../hooks/useUpdateMode'
import { getAllAnswers, saveAnswer } from '../../utils/onboardingStorage'

export default function HabitsManagementPage() {
  const submit = useSubmit()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const value = searchParams.get('value')
  const preselectedValue = value ? value : undefined
  const { isUpdate, postToReactNative } = useUpdateMode()

  // Get the selected habit from previous answers
  const allAnswers = getAllAnswers()
  const habitType = allAnswers.habits?.answer_key

  // Get question config based on habit type
  const getQuestionConfig = () => {
    switch (habitType) {
      case 'Caffeine too late':
        return {
          questionKey: 'caffeineHabit',
          heading: 'pages.onboarding.habitsManagement.caffeineHabit.heading',
          question: t(
            'pages.onboarding.habitsManagement.caffeineHabit.heading',
            { lng: 'en' },
          ),
          options: [
            {
              value: 'Before noon',
              translationKey:
                'pages.onboarding.habitsManagement.caffeineHabit.options.beforeNoon',
              emoji: '🌅',
            },
            {
              value: 'Around 2–4 pm',
              translationKey:
                'pages.onboarding.habitsManagement.caffeineHabit.options.around2to4pm',
              emoji: '🌤',
            },
            {
              value: 'After 5 pm',
              translationKey:
                'pages.onboarding.habitsManagement.caffeineHabit.options.after5pm',
              emoji: '🌙',
            },
          ],
        }

      case 'Irregular sleep schedule':
        return {
          questionKey: 'irregularScheduleHabit',
          heading:
            'pages.onboarding.habitsManagement.irregularScheduleHabit.heading',
          question: t(
            'pages.onboarding.habitsManagement.irregularScheduleHabit.heading',
            { lng: 'en' },
          ),
          options: [
            {
              value: 'Less than 1 hour',
              translationKey:
                'pages.onboarding.habitsManagement.irregularScheduleHabit.options.lessThan1Hour',
              emoji: '🕖',
            },
            {
              value: '1–2 hours',
              translationKey:
                'pages.onboarding.habitsManagement.irregularScheduleHabit.options.1to2Hours',
              emoji: '🕘',
            },
            {
              value: '3+ hours',
              translationKey:
                'pages.onboarding.habitsManagement.irregularScheduleHabit.options.moreThan3Hours',
              emoji: '🌙',
            },
            {
              value: 'Not sure yet',
              translationKey:
                'pages.onboarding.habitsManagement.irregularScheduleHabit.options.notSure',
              emoji: '🤔',
            },
          ],
        }

      case 'Alcohol at night':
        return {
          questionKey: 'alcoholHabit',
          heading: 'pages.onboarding.habitsManagement.alcoholHabit.heading',
          question: t(
            'pages.onboarding.habitsManagement.alcoholHabit.heading',
            { lng: 'en' },
          ),
          options: [
            {
              value: 'Sometimes',
              translationKey:
                'pages.onboarding.habitsManagement.alcoholHabit.options.sometimes',
              emoji: '🍷',
            },
            {
              value: 'Often',
              translationKey:
                'pages.onboarding.habitsManagement.alcoholHabit.options.often',
              emoji: '🍾',
            },
            {
              value: 'Don’t want to answer',
              translationKey:
                'pages.onboarding.habitsManagement.alcoholHabit.options.never',
              emoji: '🤔',
            },
          ],
        }

      case 'No bedtime routine':
        return {
          questionKey: 'noBedtimeRoutineHabit',
          heading:
            'pages.onboarding.habitsManagement.noBedtimeRoutineHabit.heading',
          question: t(
            'pages.onboarding.habitsManagement.noBedtimeRoutineHabit.heading',
            { lng: 'en' },
          ),
          options: [
            {
              value: 'Watch TV/Netflix',
              translationKey:
                'pages.onboarding.habitsManagement.noBedtimeRoutineHabit.options.watchingTV',
              emoji: '📺',
            },
            {
              value: 'Work/study late',
              translationKey:
                'pages.onboarding.habitsManagement.noBedtimeRoutineHabit.options.workingLate',
              emoji: '💻',
            },
            {
              value: 'Nothing specific',
              translationKey:
                'pages.onboarding.habitsManagement.noBedtimeRoutineHabit.options.nothing',
              emoji: '❓',
            },
          ],
        }

      default:
        return null
    }
  }

  const config = getQuestionConfig()

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!config) return

    const formData = new FormData(event.currentTarget)
    const selectedValue = formData.get('answer') as string

    if (!selectedValue) return

    const answer = {
      question_key: config.questionKey,
      question: config.question,
      answer_key: selectedValue,
      answer: selectedValue,
    }

    if (isUpdate) {
      postToReactNative(answer)
    } else {
      saveAnswer(config.questionKey, answer)
      submit(event.currentTarget)
    }
  }

  // If no habit is selected, show error message
  if (!config) {
    return (
      <FeedbackPage
        emoji="💡"
        heading={t('pages.onboarding.habitsManagement.screenTimeHabit.heading')}
        text={t(
          'pages.onboarding.habitsManagement.screenTimeHabit.description',
        )}
        onSubmit={handleSubmit}
      />
    )
  }

  // Transform options to the format expected by QuestionPage
  const buttons = config.options.map((option) => ({
    text: `${option.emoji} ${t(option.translationKey)}`,
    value: option.value,
  }))

  return (
    <QuestionPage
      heading={t(config.heading)}
      buttons={buttons}
      onSubmit={handleSubmit}
      headingSize="text-3xl text-center whitespace-pre-line"
      buttonTextSize="text-xl"
      buttonBgColor="bg-white"
      preselectedValue={preselectedValue}
    />
  )
}
